import { useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useDocumentDir } from './useOverlay';

/**
 * Small label for icon-only controls (the collapsed sidebar rail). Shows on
 * hover AND keyboard focus (PRD §4.2), beside the control on its inline-end
 * side. Solid surface (too small for glass, PRD §3.8). Visual only: the
 * control keeps its own accessible name, so the tooltip is `aria-hidden`.
 */
export default function Tooltip({ label, children, className = '' }: { label: string; children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const dir = useDocumentDir();
  const [pos, setPos] = useState<{ top: number; x: number } | null>(null);

  const show = () => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    setPos({ top: r.top + r.height / 2, x: dir === 'rtl' ? window.innerWidth - r.left + 8 : r.right + 8 });
  };
  const hide = () => setPos(null);

  return (
    <div
      ref={ref}
      data-tooltip-owner
      className={className}
      onPointerEnter={show}
      onPointerLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {pos && createPortal(
        <div
          aria-hidden="true"
          className="pointer-events-none fixed z-popover -translate-y-1/2 whitespace-nowrap rounded-lg bg-surface-raised px-2.5 py-1.5 text-xs font-medium text-text shadow-elevated"
          style={dir === 'rtl' ? { top: pos.top, right: pos.x } : { top: pos.top, left: pos.x }}
        >
          {label}
        </div>,
        document.body,
      )}
    </div>
  );
}
